import { normalizeArtifactRef } from 'flare-chat-core/contracts/context/artifact-ref';
import { selectCanvasArtifactTab } from './artifactBindingProjection.js';
import { projectSourcingCandidate } from './sourcingCandidateProjection.js';

// Supported wire values come from FLARE's sourcing_result Artifact contract.
const RESULT_STATUSES = new Set(['completed', 'partial']);
const RESULT_ROLES = new Set(['candidate', 'reference']);
const hasText = (value) => typeof value === 'string' && value.trim().length > 0;
const canonicalCandidate = (candidate) => RESULT_ROLES.has(candidate?.result_role)
  && [candidate?.id, candidate?.title, candidate?.source_ref?.id].every(hasText);

function validDocumentReference(document) {
  try {
    const ref = normalizeArtifactRef(document?.artifact_ref);
    return ref.artifact_type === 'sourcing_result'
      && document.artifact_schema === ref.artifact_type && document.schema_version === 1
      && ['artifact_id', 'artifact_type'].every((key) => document[key] === ref[key]);
  } catch {
    return false;
  }
}

/** Translate an already-bound immutable body; never admit, re-score or reconstruct results. */
export function projectSavedSourcingEvidence(document) {
  if (!validDocumentReference(document) || !Array.isArray(document.candidates)
    || !document.candidates.every(canonicalCandidate) || !RESULT_STATUSES.has(document.result_status)
    || document.execution?.final_status !== document.result_status) return [];
  return document.candidates.map((candidate, index) => projectSourcingCandidate({
    candidate, index, query: document.query, runId: document.run_id, eventSourceMeta: document.execution,
  })).map((row) => ({
    ...row,
    // References remain reference-role rows inside this sourcing Artifact's evidence surface.
    evidence_kind: 'sourcing',
    artifact_ref: document.artifact_ref,
    key: JSON.stringify([document.artifact_ref.scope.tenant_id, document.artifact_ref.scope.project_id,
      document.artifact_id, document.artifact_ref.artifact_version_id, row.result_id]),
  }));
}

const SAVED_EVIDENCE_INPUTS = new Map([
  ['sourcing_result', (artifactDocument) => ({
    artifactDocument: artifactDocument ?? null, timelineItems: [], evidenceTabKey: 'sourcing',
  })],
]);

/** Resolve the source once so both rows and summary cannot borrow a different version's events. */
export function resolveSavedSourcingEvidenceInput({
  artifactDocument, artifactTabProjection, canvasActiveTabKey, timelineItems,
} = {}) {
  const tab = selectCanvasArtifactTab({ artifactTabProjection, canvasActiveTabKey });
  if (artifactTabProjection !== undefined && (!tab || !tab.metadata?.artifact_ref)) {
    return { artifactDocument: null, timelineItems: [] };
  }
  const translate = SAVED_EVIDENCE_INPUTS.get(tab?.metadata?.artifact_ref?.artifact_type);
  return translate?.(artifactDocument) ?? { timelineItems };
}
